/**
 * AppsDemo — empty-state terminal mockup for the Apps list page.
 *
 * Sibling of WorkflowDemo. Same animation engine (typed command, then
 * line-by-line output), same gss-* CSS classes, same BrowserWindow
 * chrome. Only steps differ — instead of workflow scaffold/run/deploy,
 * we show the analogous app catalog/deploy/status cycle.
 *
 * The footer link points to the apps marketplace inside the current
 * project (or the public marketplace when no projectId in scope).
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { cubeOutline } from 'ionicons/icons';
import BrowserWindow from '../../atoms/BrowserWindow';
import { projectAppsMarketplacePath } from '../../../utils/marketplacePaths';
import './TestRuns.css';

/**
 * Build the demo steps with the CURRENT project's real UUID inlined
 * into the deploy command. AppsPage always mounts inside ProjectLayout
 * so projectId is in the URL — passing it in here means the command
 * the user sees is the exact command they'd run if they copied it.
 *
 * When projectId is missing (e.g. a future top-level /demo render),
 * fall back to the `<your-project-id>` placeholder so the line still
 * reads as a placeholder rather than a copy-paste landmine.
 */
function buildSteps(projectId) {
  const projectArg = projectId || '<your-project-id>';
  return [
    {
      command: 'npm install -g @zibby/cli',
      output: [
        { text: 'Resolving dependencies...', cls: '' },
        { text: 'added 142 packages in 8s', cls: '' },
        { text: '✓ @zibby/cli installed', cls: 'gss-success' },
      ],
    },
    {
      command: 'zibby app templates',
      output: [
        { text: '◯ Browsing the Zibby app catalog…', cls: '' },
        { text: '  grafana      metrics dashboards         (light  · $0.05/hr)', cls: '' },
        { text: '  drawio       diagrams + flowcharts      (light  · $0.05/hr)', cls: '' },
        { text: '  gastown      multi-agent workspace     (light  · $0.05/hr)', cls: '' },
        { text: '  open-webui   ChatGPT-style UI          (heavy  · $0.25/hr)', cls: '' },
      ],
    },
    {
      command: `zibby app deploy grafana --project ${projectArg} --name dashboards`,
      output: [
        { text: '⬆ Provisioning grafana on Fargate…', cls: '' },
        { text: '  ECS service + EFS volume + ALB target group', cls: '' },
        { text: '  agent-ops sidecar starting…', cls: '' },
        { text: '✓ Deployed (instanceId: <generated>)', cls: 'gss-success' },
        { text: '→ Public URL:  https://<generated>.apps.zibby.dev', cls: 'gss-link-line' },
      ],
    },
    {
      command: 'zibby app status <instance-id>',
      output: [
        { text: '● dashboards  (grafana v11.4.0)', cls: '' },
        { text: '  ┌ status        running (1/1)         ✓', cls: 'gss-success' },
        { text: '  ├ resources     0.5 vCPU · 1 GB RAM   ✓', cls: 'gss-success' },
        { text: '  └ hourly cost   $0.05/hr              ✓', cls: 'gss-success' },
      ],
    },
  ];
}

const CHAR_SPEED = 18;
const OUTPUT_LINE_DELAY = 120;
const STEP_PAUSE = 300;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const AppsDemo = () => {
  const { projectId } = useParams();
  // Inline the URL's projectId into the deploy command. Memo so the
  // animation's steps reference is stable across re-renders (otherwise
  // changing identity each render would retrigger the useEffect's
  // typing animation).
  const steps = useMemo(() => buildSteps(projectId), [projectId]);
  const [visibleSteps, setVisibleSteps] = useState([]);
  const cancelRef = useRef(false);
  const timeoutsRef = useRef([]);

  const clearAllTimeouts = useCallback(() => {
    cancelRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const delay = useCallback((ms) => {
    return new Promise((resolve) => {
      const t = setTimeout(() => {
        if (!cancelRef.current) resolve();
      }, ms);
      timeoutsRef.current.push(t);
    });
  }, []);

  useEffect(() => {
    cancelRef.current = false;

    const animate = async () => {
      // Wait for the wrapper's CSS fade-in (320ms) to finish before
      // typing — see WorkflowDemo for the same comment.
      await delay(380);
      for (let si = 0; si < steps.length; si++) {
        if (cancelRef.current) return;
        const step = steps[si];

        for (let ci = 1; ci <= step.command.length; ci++) {
          if (cancelRef.current) return;
          setVisibleSteps((prev) => {
            const next = [...prev];
            next[si] = { commandText: step.command.slice(0, ci), outputLines: [], done: false };
            return next;
          });
          await delay(CHAR_SPEED);
        }

        for (let oi = 0; oi < step.output.length; oi++) {
          if (cancelRef.current) return;
          await delay(OUTPUT_LINE_DELAY);
          setVisibleSteps((prev) => {
            const next = [...prev];
            const current = { ...next[si] };
            current.outputLines = [...current.outputLines, step.output[oi]];
            next[si] = current;
            return next;
          });
        }

        setVisibleSteps((prev) => {
          const next = [...prev];
          next[si] = { ...next[si], done: true };
          return next;
        });

        if (si < steps.length - 1) {
          await delay(STEP_PAUSE);
        }
      }
    };

    animate();
    return clearAllTimeouts;
  }, [delay, clearAllTimeouts, steps]);

  return (
    <div className="gss-wrapper">
      <div className="gss-header">
        <IonIcon icon={cubeOutline} className="gss-header-icon" />
        <span className="gss-header-text">
          Deploy a hosted app in 4 steps — or browse the{' '}
          <Link
            className="gss-header-link"
            to={projectId ? projectAppsMarketplacePath(projectId) : '/marketplace'}
          >
            app marketplace
          </Link>.
        </span>
      </div>
      <BrowserWindow variant="dark" className="gss-terminal">
        <div className="gss-terminal-body">
          {steps.map((step, si) => {
            const vis = visibleSteps[si];
            if (!vis) return null;
            return (
              <div key={si} className="gss-step">
                <div className="gss-command">
                  <span className="gss-prompt">$</span>
                  <span className="gss-command-text">
                    {vis.commandText}
                    {!vis.done && vis.outputLines.length === 0 && <span className="gss-cursor">|</span>}
                  </span>
                  {vis.done && (
                    <button
                      className="gss-copy-btn"
                      onClick={() => navigator.clipboard?.writeText(step.command)}
                      aria-label="Copy command"
                    >
                      <CopyIcon />
                    </button>
                  )}
                </div>
                {vis.outputLines.length > 0 && (
                  <div className="gss-output">
                    {vis.outputLines.map((line, li) => (
                      <span key={li} className={`gss-output-line gss-indent ${line.cls}`}>
                        {line.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </BrowserWindow>
    </div>
  );
};

export default AppsDemo;
